const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d] as const
const PDF_FILE_NAME_PATTERN = /\.pdf$/i
const PDF_MIME_TYPES = new Set(['application/pdf'])
const GENERIC_UPLOAD_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'application/x-pdf',
])

export const PDF_FILE_ACCEPT = '.pdf,application/pdf'

type PdfReadable = Blob | {
  arrayBuffer: () => Promise<ArrayBuffer>
  text?: () => Promise<string>
}

const normalizeMimeType = (value: string) => value.trim().toLowerCase()

export const isPdfFileName = (fileName: string) =>
  PDF_FILE_NAME_PATTERN.test(fileName.trim())

export const isPdfUploadMimeType = (mimeType: string) => {
  const normalizedMimeType = normalizeMimeType(mimeType)

  return (
    PDF_MIME_TYPES.has(normalizedMimeType) ||
    GENERIC_UPLOAD_MIME_TYPES.has(normalizedMimeType)
  )
}

export const hasPdfSignature = (bytes: Uint8Array) =>
  bytes.length >= PDF_SIGNATURE.length &&
  PDF_SIGNATURE.every((byte, index) => bytes[index] === byte)

const readBlobArrayBuffer = async (blob: PdfReadable) => {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer()
  }

  if (blob instanceof Blob && typeof FileReader !== 'undefined') {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
          return
        }

        reject(new TypeError('Failed to read blob as ArrayBuffer'))
      }

      reader.onerror = () => {
        reject(reader.error ?? new TypeError('Failed to read blob as ArrayBuffer'))
      }

      reader.readAsArrayBuffer(blob)
    })
  }

  if (typeof blob.text === 'function') {
    return new TextEncoder().encode(await blob.text()).buffer
  }

  if (!(blob instanceof Blob)) {
    throw new TypeError('Failed to read blob as ArrayBuffer')
  }

  return new Response(blob).arrayBuffer()
}

export const fileHasPdfSignature = async (file: PdfReadable) => {
  const bytes = new Uint8Array(await readBlobArrayBuffer(file))

  return hasPdfSignature(bytes.subarray(0, PDF_SIGNATURE.length))
}

export const isAcceptedClientPdfFile = (file: File) =>
  isPdfFileName(file.name) && isPdfUploadMimeType(file.type)
